import React, { useCallback, useEffect, useState } from 'react';

import {
  ExclamationCircleOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { Grid, Stack } from '@lp/ds-next';
import { Tag, Typography, Space, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import get from 'lodash/get';

import PendingV2Icon from '../../../../components/icons/PendingV2Icon';
import ValidatedIcon from '../../../../components/icons/Validated';
import CreatePartner from '../components/CreatePartner';
import ImportCSVFile from '../components/ImportCSVFile';
import { LPPartnerFilterForm } from '../components/LPParnterFilterForm';
import useGetPartners from '../hooks/useGetPartners';
import useLPPartnersStore, {
  TLPPartnersSortBy,
} from '../hooks/useLPPartnersStore';
import BlockedIcon from '@/components/icons/BlockedIcon';
import Refresh from '@/components/icons/Refresh';
import StyledTable from '@/components/tables/StyledTable';
import TablesTotals from '@/components/TablesTotals';
import { useGetPartnersActivity } from '@/features/gdpr/partnerClosure/api/getPartnersActivity';
import { useGetPartnersUnpaidBills } from '@/features/gdpr/partnerClosure/api/getPartnersUnpaidBills';
import { ClosureRequestStatusTag } from '@/features/gdpr/partnerClosure/components/ClosureRequestStatusTag';
import {
  ClosureRequest,
  closureRequestStatuses,
} from '@/features/gdpr/partnerClosure/types';
import {
  getCurrentPage,
  getOffset,
  parseAntdSorter,
} from '@/helpers/antdTable';
import { formatStringToDate } from '@/helpers/dates';
import useAntdColumns from '@/hooks/useAntdColumns';
import useWindowSize from '@/hooks/useWindowSize';
import useGetTotals from '@/shared/hooks/useGetTotals';
import { capitalizeFirstLetter } from '@/utils/format';

// Create constant object with statusIcon
const statusIcon = {
  VALIDATED: <ValidatedIcon />,
  PENDING: <PendingV2Icon />,
  BLOCKED: <BlockedIcon />,
} as const;

const { Text } = Typography;

const LPPartnersTable = () => {
  const windowSize = useWindowSize();

  useGetPartners();
  const { getVillageTotal } = useGetTotals();

  const rowData = useLPPartnersStore((state) => state.rowData);
  const loading = useLPPartnersStore((state) => state.loading);
  const pageSize = useLPPartnersStore((state) => state.pageSize);
  const offset = useLPPartnersStore((state) => state.offset);
  const total = useLPPartnersStore((state) => state.total);
  const defaultSort = useLPPartnersStore((state) => state.defaultSort);
  const dispatch = useLPPartnersStore((state) => state.dispatch);

  const [vTotal, setVTotal] = useState<number>(0);

  const currentPartnerIds = rowData.map((row) => row.id);

  const { data: partnersActivity = {} } = useGetPartnersActivity({
    partnerIds: currentPartnerIds,
  });

  const { data: partnersHasUnpaidBills = {} } = useGetPartnersUnpaidBills({
    partnerIds: currentPartnerIds,
  });

  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [dispatch]);

  useEffect(() => {
    const asyncWrapper = async () => {
      const total = await getVillageTotal();
      setVTotal(total);
    };
    asyncWrapper();
  }, [getVillageTotal]);

  const PartnerStatusCell = ({
    partnerId,
    closureRequest,
  }: {
    partnerId: string;
    closureRequest?: ClosureRequest | null;
  }) => {
    const isClosureRequestApproved =
      closureRequest?.status === closureRequestStatuses.Approved;
    const isClosureRequestAnonymized =
      closureRequest?.status === closureRequestStatuses.Anonymized;

    const shouldHideTags =
      isClosureRequestApproved || isClosureRequestAnonymized;

    const isInactive = partnersActivity[partnerId]?.is_active === false;

    return (
      <Stack flexDirection="column" gap="0.2rem">
        {!shouldHideTags && isInactive && (
          <Tag
            data-testid="tag-inactivity"
            icon={<ExclamationCircleOutlined />}
            color="red"
            style={{ width: 'fit-content' }}
          >
            Inactivity
          </Tag>
        )}
        {!shouldHideTags && partnersHasUnpaidBills[partnerId] && (
          <Tag
            data-testid="tag-unpaid-bills"
            icon={<ExclamationCircleOutlined />}
            color="red"
            style={{ width: 'fit-content' }}
          >
            Unpaid bills
          </Tag>
        )}
      </Stack>
    );
  };

  const columns: ColumnProps<any>[] = useAntdColumns({
    columnsKeys: [
      'clientId',
      'name',
      'updatedAt',
      'villageServiceLevel',
      'businessType',
      'isInternal',
      'businessStatus',
      'PartnerIDCard',
    ],
    defaultSort: defaultSort,
    eyeLinkProps: { to: '/lp-village/partners', width: '1.5rem' },
    columnsSpecialProps: {
      clientId: {
        render: (_text, record) => {
          if (record.clientId) {
            return (
              <Text copyable={{ text: record.clientId }}>
                {record.clientId}
              </Text>
            );
          }
        },
      },
      isInternal: {
        title: 'Partner profile',
        render: (_text, record) =>
          record.isInternal ? 'Internal' : 'External',
      },
      businessType: {
        title: 'Account type',
        render: (_text, record) =>
          capitalizeFirstLetter(record.businessInformation.type),
      },
      businessStatus: {
        title: 'Account status',
        render: (_text, record) => (
          <Stack flexDirection="column" gap="0.2rem">
            <Stack flexDirection="row" gap="0.2rem" alignItems="center">
              {statusIcon[record.businessInformation.status]}
              {capitalizeFirstLetter(record.businessInformation.status)}
            </Stack>
            <PartnerStatusCell
              partnerId={record.id}
              closureRequest={
                record.closureRequest
                  ? {
                      status: record.closureRequest.Status,
                      reason: record.closureRequest.Reason,
                      details: record.closureRequest.Details,
                    }
                  : null
              }
            />
            {record.closureRequest && (
              <ClosureRequestStatusTag
                closureRequest={
                  {
                    status: record.closureRequest.Status,
                    reason: record.closureRequest.Reason,
                    details: record.closureRequest.Details,
                  } as ClosureRequest
                }
              />
            )}
            {record.archived && (
              <Tag
                data-testid="tag-archived"
                icon={<FolderOpenOutlined />}
                color="red"
                style={{ width: 'fit-content' }}
              >
                Archived
              </Tag>
            )}
          </Stack>
        ),
      },
      PartnerIDCard: {
        title: 'Partner ID Card',
        render: (_text, record) => (
          <Space>
            <Tag
              color={record.isPartnerIDCardCompleted ? '  #E9F5F1' : '#F4F4F4'}
              style={{
                fontSize: '0.75rem',
                fontWeight: 400,
              }}
            >
              <Text
                strong
                style={{
                  color: record.isPartnerIDCardCompleted
                    ? '#0CA86D'
                    : '#3F3F3F',
                }}
              >
                {record.isPartnerIDCardCompleted
                  ? 'Completed'
                  : 'Not completed'}
              </Text>
            </Tag>
            {record.isPartnerIDCardCompleted && (
              <Tag
                style={{ display: 'flex', alignItems: 'center' }}
                color="red"
              >
                <Tooltip
                  title={
                    record.erpSynchronizedAt ? (
                      <Text>
                        Last synchronisation date:
                        {` ${formatStringToDate(record.erpSynchronizedAt)} `}
                      </Text>
                    ) : (
                      <Text>Not synchronized</Text>
                    )
                  }
                  color="#FFFFFF"
                >
                  <Refresh style={{ height: '1.25rem' }} />
                </Tooltip>
              </Tag>
            )}
          </Space>
        ),
      },
    },
  });

  const pagination = {
    current: getCurrentPage(pageSize, offset),
    pageSize,
    total,
    showSizeChanger: true,
  };

  const handleTableChange = useCallback(
    (pagination, _filters, sorter) => {
      const [sortField, sortOrder] = parseAntdSorter(sorter);
      dispatch({
        type: 'updateSorting',
        args: { sortBy: sortField as TLPPartnersSortBy, order: sortOrder },
      });
      dispatch({
        type: 'updatePagination',
        args: {
          offset: getOffset(pagination.pageSize, pagination.current),
          pageSize: pagination.pageSize,
        },
      });
    },
    [dispatch]
  );

  return (
    <Grid container justifyContent="center" px="5rem">
      <Grid item mobile={12}>
        <LPPartnerFilterForm dispatch={dispatch} />
      </Grid>
      <Grid item mobile={12}>
        <Grid container gap={2}>
          <Grid item container>
            <Grid
              item
              container
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
            >
              <Grid item>
                <ImportCSVFile
                  buttonText="Import Late Shops CSV"
                  modalTitle="Import List of Late Shops"
                />
              </Grid>
              <Grid item>
                <CreatePartner />
              </Grid>
            </Grid>
          </Grid>
          <Grid item mobile={12}>
            <StyledTable
              rowKey="id"
              dataSource={rowData}
              columns={columns}
              size="small"
              pagination={pagination}
              loading={loading}
              scroll={{ y: get(windowSize, 'height', 0) * 0.65 }}
              onChange={handleTableChange}
              footer={() => <TablesTotals total={total} gTotal={vTotal} />}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LPPartnersTable;
